import { DOCTOR_MODAL_TYPE, useModalStore } from '@modal'
import { ColumnDef } from '@tanstack/react-table'
import { Badge, Table2 } from '@ui'
import parse from 'html-react-parser'
import { useMemo } from 'react'
import { palette } from '../../../../../themes/palette'
import {
  ImagingOrderData,
  ImagingOrderIncludedImagingResult,
  ImagingOrderIncludedResource
} from '../../../../../types/imaging_order.type'
import { patientHistoryTableClassName } from './tableStyles'

type Props = {
  records: ImagingOrderData[]
  included?: ImagingOrderIncludedResource[]
  isLoading?: boolean
}

const isImagingResult = (inc: ImagingOrderIncludedResource): inc is ImagingOrderIncludedImagingResult =>
  inc.type === 'imaging_result'

export default function ImagingResultsTable({ records, included = [], isLoading }: Props) {
  const { openModal } = useModalStore()

  // The order's own `study_names` + its sideloaded `imaging_result`s (both come straight off
  // GET /patient/imaging_orders) replace the old two-hop order -> orderable(imaging) catalog
  // lookup that only worked against the staff /imaging_results endpoint.
  const resultsFor = (order: ImagingOrderData): ImagingOrderIncludedImagingResult[] => {
    const rel = order.relationships.imaging_results
    const ids = 'data' in rel ? rel.data.map(ref => ref.id) : []
    return included.filter(
      (inc): inc is ImagingOrderIncludedImagingResult => isImagingResult(inc) && ids.includes(inc.id)
    )
  }

  const columns = useMemo<ColumnDef<ImagingOrderData>[]>(() => {
    return [
      {
        accessorKey: 'test',
        header: 'Test',
        cell: ({ row }) => <span>{row.original.attributes.study_names?.join(', ') || '-'}</span>
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge label={row.original.attributes.status?.replace(/_/g, ' ')} status={row.original.attributes.status} />
        )
      },
      {
        accessorKey: 'date',
        header: 'Date',
        cell: ({ row }) => (
          <span>
            {row.original.attributes.created_at
              ? new Date(row.original.attributes.created_at).toLocaleDateString()
              : '-'}
          </span>
        )
      },
      {
        accessorKey: 'interpretation',
        header: 'Interpretation',
        cell: ({ row }) => {
          const interpretation = resultsFor(row.original).find(result => result.attributes.interpretation)?.attributes
            .interpretation
          return <Badge label={interpretation ? parse(interpretation) : '-'} />
        }
      },
      {
        accessorKey: 'view',
        header: '',
        cell: ({ row }) => {
          const result = resultsFor(row.original)[0]
          if (!result) return <span>-</span>
          return (
            <span
              onClick={() =>
                openModal(DOCTOR_MODAL_TYPE.IMAGING_RESULT_PREVIEW, { defaultData: result, id: result.id })
              }
              style={{ cursor: 'pointer', fontWeight: 500, textDecoration: 'underline', color: palette.primary }}
            >
              View result
            </span>
          )
        }
      }
    ]
  }, [included])

  return (
    <Table2
      className={patientHistoryTableClassName}
      columns={columns}
      data={records}
      emptyDescription="No results found."
      isLoading={isLoading}
      message="No results found"
      showPagination={false}
      wrapperClass="overflow-x-auto"
    />
  )
}
